import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { paper, fonts, easing, bp } from "../theme";
import { useWebSocket } from "../useWebSocket";
import type { SelectArticleView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsEyebrow, MsRule, MsChip,
    MsPaper, MsBtn, MsPlayerButton,
    MsSourceNote,
    Dots,
} from "../ms";

/* The spring the chosen card rides from its grid slot to the centre. A touch of
 * overshoot so it settles rather than just stops. Shared by both ends of the
 * `picked-article` morph so the layout animation is one motion. */
const cardLayoutTransition = { type: "spring" as const, stiffness: 220, damping: 24 };

/* ── Columns — the two states of the screen ───────────────────────────────
 * Both live inside one AnimatePresence so the grid can cross-fade out as the
 * chosen card settles in, and — crucially — so the `picked-article` morph is
 * reliable. A bare layoutId bridging a full `return <Other/>` tree swap fires
 * only sometimes; keyed views under AnimatePresence (as in PromptEntry) is the
 * pattern that fires every time. */
const ChoosingColumn = styled(motion.div)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
`;

const ChosenColumn = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 100%;
`;

const HeroCard = styled(motion.div)`
    width: 100%;
    max-width: 380px;
    margin: 0 auto;
`;

const WaitingBlock = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

const AwaitingChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
`;

const PromptText = styled.div`
    font-family: ${fonts.serif};
    font-weight: 600;
    font-size: 22px;
    line-height: 1.18;
    color: ${paper.ink};
`;

const PromptRule = styled(MsRule)`
    margin: 14px 0 8px;
`;

const LoadingText = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    color: ${paper.inkMute};
`;

/* ── The card ─────────────────────────────────────────────────────────────
 * Structure matters here: CardShell carries the `layoutId` and NOTHING else —
 * framer owns its transform for the morph. The tilt and hover-straighten live
 * on CardTilt *inside* it, so they never fight the layout animation (a transform
 * on the layoutId element is what made the morph janky before). */
const CardShell = styled(motion.div)`
    height: 100%;
`;

/* `.card-tilt` class so SourceBtn's hover can straighten it with a plain
 * selector — emotion component selectors (`${CardTilt}`) need a babel/swc plugin
 * this project doesn't run, and throw at runtime. */
const CardTilt = styled.div<{ tilt: number }>`
    height: 100%;
    transform: rotate(${p => p.tilt}deg);
    transition: transform 0.16s ${easing.pop};
`;

const CardEyebrow = styled(MsEyebrow)`
    margin-bottom: 6px;
`;

const CardTitle = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 22px;
    line-height: 1.15;
    letter-spacing: -0.005em;
    color: ${paper.ink};
`;

const CardRule = styled(MsRule)`
    margin: 10px 0;
    opacity: 0.3;
`;

/* The source credit — same on the grid card and the chosen card, and read off
 * the article rather than written here. Rebased on `MsSourceNote`, which this
 * had arrived at independently. */
const SourceCaption = styled(MsSourceNote)`
    font-size: 14px;
    color: ${paper.inkSoft};
`;

/* The unstyled full-width wrapper, which MsPlayerButton already is — this had
 * redeclared it byte for byte. Rebasing keeps the card's own drop animation and
 * inherits the press haptic. */
const SourceBtn = styled(MsPlayerButton)<{ delay: number }>`
    animation: ms-paper-drop 0.7s ${easing.pop} both;
    animation-delay: ${p => p.delay}s;

    &:hover .card-tilt {
        transform: rotate(0deg) translateY(-2px);
    }
`;

const SourcesGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media ${bp.wide} {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 22px;
    }
`;

/* ──────────────────────────────────────────────────────────────────────
 * Article selection has two states driven entirely from the snapshot:
 *
 *   1. The picker grid (default) — article cards to choose from; each click
 *      sends `select_article`.
 *   2. The "you chose, awaiting others" state — server sets `isWaiting` and
 *      `selectedArticle` after the click.
 *
 * The clicked card is the *same* card, just centred: it floats up from its grid
 * slot to the middle via `layoutId="picked-article"`, while the grid cross-fades
 * away and the "waiting" line settles in beneath it.
 * ──────────────────────────────────────────────────────────────────── */
export default function ArticleSelection({ view }: { view: SelectArticleView }) {
    const { sendMessage } = useWebSocket();
    const { articles, reshuffleAvailable, prompt, isWaiting, selectedArticle, awaitingPlayers } = view;

    /** Optimistic: mark the clicked card with the morph layoutId immediately, so
     *  the "from" of the animation is captured before the server's confirming
     *  snapshot swaps us to the chosen state. */
    const [pendingIndex, setPendingIndex] = useState<number | null>(null);

    useEffect(() => {
        // Clear the optimistic mark once the server confirms via isWaiting.
        if (!isWaiting) setPendingIndex(null);
    }, [isWaiting]);

    const handleSelect = (index: number) => {
        setPendingIndex(index);
        sendMessage({ type: "select_article", index });
    };
    const handleReshuffle = () => sendMessage({ type: "reshuffle_articles" });

    const chosen = selectedArticle != null;
    const awaiting = awaitingPlayers ?? [];

    return (
        <MsScreen>
            <MsBody wide style={{ justifyContent: chosen ? "center" : "flex-start", gap: 18 }}>
                <AnimatePresence mode="popLayout" initial={false}>
                    {chosen ? (
                        <ChosenColumn
                            key="chosen"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { duration: 0.3 } }}
                            exit={{ opacity: 0, transition: { duration: 0.15 } }}
                        >
                            <HeroCard layoutId="picked-article" transition={cardLayoutTransition}>
                                <MsPaper>
                                    <CardEyebrow tone="red">Your source</CardEyebrow>
                                    <CardTitle>{selectedArticle.title}</CardTitle>
                                    <CardRule />
                                    <SourceCaption>{selectedArticle.attribution}</SourceCaption>
                                </MsPaper>
                            </HeroCard>

                            <WaitingBlock
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0, transition: { delay: 0.28, duration: 0.35, ease: [0.22, 1, 0.36, 1] } }}
                            >
                                <MsEyebrow tone="mute">
                                    {awaiting.length > 0 ? <>Waiting on others <Dots /></> : "Everyone's locked in"}
                                </MsEyebrow>
                                {awaiting.length > 0 && (
                                    <AwaitingChips>
                                        <AnimatePresence mode="popLayout">
                                            {awaiting.map(p => (
                                                <MsChip
                                                    key={p.id}
                                                    layout
                                                    initial={{ opacity: 0, scale: 0.85 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.85 }}
                                                >
                                                    {p.name}
                                                </MsChip>
                                            ))}
                                        </AnimatePresence>
                                    </AwaitingChips>
                                )}
                            </WaitingBlock>
                        </ChosenColumn>
                    ) : (
                        <ChoosingColumn
                            key="choosing"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { duration: 0.3 } }}
                            exit={{ opacity: 0, transition: { duration: 0.18 } }}
                        >
                            <motion.div layoutId="prompt-text">
                                <PromptText>
                                    "{prompt.replace(/___/g, "_____")}"
                                </PromptText>
                                <PromptRule />
                            </motion.div>

                            <MsEyebrow>Pick your article</MsEyebrow>

                            <SourcesGrid>
                                {articles.length === 0 && (
                                    <LoadingText>Loading articles…</LoadingText>
                                )}
                                {articles.map((article, i) => (
                                    <SourceBtn
                                        key={article.title}
                                        delay={0.2 + i * 0.08}
                                        haptic="select"
                                        onClick={() => handleSelect(i)}
                                    >
                                        <CardShell
                                            layoutId={i === pendingIndex ? "picked-article" : undefined}
                                            transition={cardLayoutTransition}
                                        >
                                            <CardTilt className="card-tilt" tilt={i % 2 === 0 ? -0.6 : 0.6}>
                                                <MsPaper>
                                                    <CardEyebrow tone="red">
                                                        Article {String.fromCharCode(65 + i)}
                                                    </CardEyebrow>
                                                    <CardTitle>{article.title}</CardTitle>
                                                    <CardRule />
                                                    <SourceCaption>{article.attribution}</SourceCaption>
                                                </MsPaper>
                                            </CardTilt>
                                        </CardShell>
                                    </SourceBtn>
                                ))}
                            </SourcesGrid>
                        </ChoosingColumn>
                    )}
                </AnimatePresence>
            </MsBody>
            {!chosen && reshuffleAvailable && (
                <MsFooter wide>
                    <MsBtn onClick={handleReshuffle}>Reshuffle hand</MsBtn>
                </MsFooter>
            )}
        </MsScreen>
    );
}
